/*
 * hyperion-index:begin
 * ---
 * path: hyperion-cloud/web/src/capabilities/credential-health/pages/CredentialHealthRemediation.tsx
 * layer: web
 * area: credential-health
 * kind: page
 * web_root: pages
 * relpath: web/src/capabilities/credential-health/pages/CredentialHealthRemediation.tsx
 * filename: CredentialHealthRemediation.tsx
 * extension: tsx
 * ---
 * hyperion-index:end
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  HandThumbUpIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { api, getErrorMessage } from '@/platform/api-client/client';
import { confirmAction } from '@/platform/design-system/components/shared/confirmAction';
import { clsx } from 'clsx';
import { formatRelativeTime } from '@/platform/shared/lib/utils';
import { Link } from 'react-router-dom';
import { PageEmptyState } from '@/platform/design-system/components/ui/PageEmptyState';
import { DegradedState } from '@/platform/design-system/components/ui/DegradedState';
import { Wrench } from 'lucide-react';

// ---------- Types ----------

interface RemediationTask {
  id: string;
  credentialId: string;
  remediationType: string;
  status: string;
  requiresApproval: boolean;
  approvedBy: string | null;
  approvedAt: string | null;
  deviceId: string | null;
  serviceName: string;
  oldAccount: string;
  newAccount: string;
  errorMessage: string;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
}

const REMEDIATION_ACTIONS: Record<string, string> = {
  rotate_ad_password: 'rotate the AD password',
  rotate_local_password: 'rotate the local password',
  rotate_sql_login: 'rotate the SQL login password',
  rotate_web_portal: 'rotate the web portal password',
  create_service_account: 'create a dedicated service account',
  swap_service_account: 'swap the service account',
};

// ---------- Component ----------

export function CredentialHealthRemediation({ embedded: _embedded }: { embedded?: boolean } = {}) {
  const queryClient = useQueryClient();
  const [filterStatus, setFilterStatus] = useState<string>('all');

  const {
    data: tasks,
    isError,
    isFetching,
    isLoading,
    refetch,
  } = useQuery<RemediationTask[]>({
    queryKey: ['credential-health', 'remediation', filterStatus],
    queryFn: async () => {
      const res = await api.get<RemediationTask[] | null>(
        '/credential-health/remediation?limit=100'
      );
      // A Go nil slice arrives as null: that is an empty queue, not a failure.
      if (res.data === null) return [];
      if (!Array.isArray(res.data)) {
        throw new Error('Remediation queue response did not match its contract.');
      }
      return res.data;
    },
    refetchInterval: 10000,
  });

  const approveMutation = useMutation({
    meta: { errorFeedback: 'inline' },
    mutationFn: async (taskId: string) => {
      await api.post(`/credential-health/remediation/${taskId}/approve`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credential-health', 'remediation'] });
    },
  });

  const cancelMutation = useMutation({
    meta: { errorFeedback: 'inline' },
    mutationFn: async (taskId: string) => {
      await api.post(`/credential-health/remediation/${taskId}/cancel`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credential-health', 'remediation'] });
    },
  });

  const filteredTasks =
    tasks?.filter((t) => {
      if (filterStatus === 'all') return true;
      return t.status === filterStatus;
    }) ?? [];

  const statusCounts = {
    pending: tasks?.filter((t) => t.status === 'pending').length ?? 0,
    approved: tasks?.filter((t) => t.status === 'approved').length ?? 0,
    running: tasks?.filter((t) => t.status === 'running').length ?? 0,
    completed: tasks?.filter((t) => t.status === 'completed').length ?? 0,
    failed: tasks?.filter((t) => t.status === 'failed').length ?? 0,
  };

  const confirmApprove = async (task: RemediationTask) => {
    const action =
      REMEDIATION_ACTIONS[task.remediationType] ?? task.remediationType.replace(/_/g, ' ');
    const target = task.serviceName
      ? `${task.serviceName}${task.oldAccount ? ` (${task.oldAccount})` : ''}`
      : task.oldAccount || `credential ${task.credentialId.slice(0, 8)}`;
    const confirmed = await confirmAction({
      title: `Approve: ${action} for ${target}?`,
      message:
        'Approving queues the change for execution against the live system. The current secret stops working once it runs.',
      variant: 'danger',
      confirmLabel: 'Approve and queue',
    });
    if (confirmed) approveMutation.mutate(task.id);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <ArrowPathIcon className="h-8 w-8 motion-loading-spin text-[rgb(var(--color-text-tertiary))]" />
      </div>
    );
  }

  if (isError || !tasks) {
    return (
      <div className="page-enter p-6">
        <DegradedState
          title="Credential remediation could not be loaded"
          description="The canonical credential-health service did not return a usable queue. No clean or completed state has been inferred."
          onRetry={() => void refetch()}
          isRetrying={isFetching}
        />
      </div>
    );
  }

  return (
    <div className="page-enter space-y-6">
      {/* Header */}
      <div className="control-surface flex items-center gap-4 rounded-[1.75rem] px-5 py-5 md:px-6">
        <Link
          to="/switchboard/vault?tab=health&hsub=dashboard"
          className="control-action rounded-xl p-2"
        >
          <ArrowLeftIcon className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-[rgb(var(--color-text-primary))]">
            Remediation Queue
          </h1>
          <p className="mt-1 text-sm text-[rgb(var(--color-text-secondary))]">
            Password rotations and service account swaps
          </p>
        </div>
      </div>

      {/* Status Filter */}
      <div className="flex items-center gap-2 rounded-2xl border border-[rgb(var(--color-border-primary)/0.78)] bg-[rgb(var(--color-bg-primary)/0.72)] p-1.5">
        {(['all', 'pending', 'approved', 'running', 'completed', 'failed'] as const).map(
          (status) => {
            const count =
              status === 'all'
                ? (tasks?.length ?? 0)
                : (statusCounts[status as keyof typeof statusCounts] ?? 0);
            return (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                className={clsx(
                  'rounded-xl px-3 py-1.5 text-xs font-medium transition-[background-color,color,box-shadow] duration-200 focus:outline-none focus:ring-2 focus:ring-accent-ring focus:ring-offset-2',
                  filterStatus === status
                    ? 'bg-indigo-600 text-white shadow-[0_12px_20px_-18px_rgb(79_70_229/0.45)]'
                    : 'text-[rgb(var(--color-text-tertiary))] hover:bg-[rgb(var(--color-bg-tertiary))] hover:text-[rgb(var(--color-text-secondary))]'
                )}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
                {count > 0 && (
                  <span className="ml-1.5 text-[rgb(var(--color-text-tertiary))]">({count})</span>
                )}
              </button>
            );
          }
        )}
      </div>

      {(approveMutation.isError || cancelMutation.isError) && (
        <p
          role="alert"
          className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm text-red-400"
        >
          {getErrorMessage(approveMutation.error ?? cancelMutation.error)}
        </p>
      )}

      {/* Tasks Table */}
      <div className="control-surface overflow-hidden rounded-xl">
        {filteredTasks.length === 0 ? (
          <PageEmptyState
            stateKind="verified-empty"
            icon={Wrench}
            iconColor="text-green-600 dark:text-green-400"
            title="No remediation tasks"
            subtitle="Nothing is queued. Rotations and service-account swaps appear here once requested or when a rotation policy falls due."
            register="operational"
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[rgb(var(--color-border-primary))]">
                  <th className="px-5 py-3 text-left text-xs font-medium uppercase text-[rgb(var(--color-text-tertiary))]">
                    Type
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-medium uppercase text-[rgb(var(--color-text-tertiary))]">
                    Credential
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-medium uppercase text-[rgb(var(--color-text-tertiary))]">
                    Status
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-medium uppercase text-[rgb(var(--color-text-tertiary))]">
                    Service
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-medium uppercase text-[rgb(var(--color-text-tertiary))]">
                    Error
                  </th>
                  <th className="px-5 py-3 text-right text-xs font-medium uppercase text-[rgb(var(--color-text-tertiary))]">
                    Created
                  </th>
                  <th className="px-5 py-3 text-right text-xs font-medium uppercase text-[rgb(var(--color-text-tertiary))]">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[rgb(var(--color-border-primary)/0.5)]">
                {filteredTasks.map((task) => (
                  <tr key={task.id} className="control-list-row">
                    <td className="px-5 py-3">
                      <RemediationTypeBadge type={task.remediationType} />
                    </td>
                    <td className="px-5 py-3 font-mono text-xs text-[rgb(var(--color-text-secondary))]">
                      {task.credentialId.slice(0, 12)}...
                    </td>
                    <td className="px-5 py-3">
                      <TaskStatusBadge status={task.status} />
                    </td>
                    <td className="px-5 py-3 text-xs text-[rgb(var(--color-text-secondary))]">
                      {task.serviceName || '—'}
                      {task.oldAccount && (
                        <span className="text-[rgb(var(--color-text-tertiary))]">
                          {' '}
                          ({task.oldAccount})
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-xs text-red-400 max-w-xs truncate">
                      {task.errorMessage || '—'}
                    </td>
                    <td className="px-5 py-3 text-right text-xs text-[rgb(var(--color-text-tertiary))]">
                      {formatRelativeTime(task.createdAt)}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {task.status === 'pending' && task.requiresApproval && (
                          <button
                            onClick={() => void confirmApprove(task)}
                            disabled={approveMutation.isPending}
                            className="control-action rounded-lg p-1.5 text-emerald-500 focus:outline-none focus:ring-2 focus:ring-accent-ring focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Approve"
                          >
                            <HandThumbUpIcon className="w-4 h-4" />
                          </button>
                        )}
                        {(task.status === 'pending' || task.status === 'approved') && (
                          <button
                            onClick={() => cancelMutation.mutate(task.id)}
                            disabled={cancelMutation.isPending}
                            className="control-action rounded-lg p-1.5 text-red-500 focus:outline-none focus:ring-2 focus:ring-accent-ring focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Cancel"
                          >
                            <XMarkIcon className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- Sub-components ----------

function RemediationTypeBadge({ type }: { type: string }) {
  const labels: Record<string, { text: string; color: string }> = {
    rotate_ad_password: { text: 'AD Password', color: 'bg-accent/10 text-blue-400' },
    rotate_local_password: { text: 'Local Password', color: 'bg-cyan-500/10 text-cyan-400' },
    rotate_sql_login: { text: 'SQL Login', color: 'bg-purple-500/10 text-purple-400' },
    rotate_m365_password: { text: 'M365 Password', color: 'bg-sky-500/10 text-sky-400' },
    rotate_web_portal: { text: 'Web Portal', color: 'bg-indigo-500/10 text-indigo-400' },
    create_service_account: {
      text: 'Create Svc Acct',
      color: 'bg-emerald-500/10 text-emerald-400',
    },
    swap_service_account: { text: 'Swap Svc Acct', color: 'bg-amber-500/10 text-amber-400' },
  };

  const entry = labels[type] || {
    text: type,
    color: 'bg-[rgb(var(--color-bg-tertiary))] text-[rgb(var(--color-text-tertiary))]',
  };

  return (
    <span className={clsx('px-2 py-0.5 text-xs font-medium rounded', entry.color)}>
      {entry.text}
    </span>
  );
}

function TaskStatusBadge({ status }: { status: string }) {
  const config: Record<string, { icon: React.ElementType; color: string }> = {
    pending: { icon: ClockIcon, color: 'text-[rgb(var(--color-text-tertiary))]' },
    approved: { icon: HandThumbUpIcon, color: 'text-emerald-400' },
    running: { icon: ArrowPathIcon, color: 'text-blue-400' },
    completed: { icon: CheckCircleIcon, color: 'text-emerald-400' },
    failed: { icon: XCircleIcon, color: 'text-red-400' },
    cancelled: { icon: XMarkIcon, color: 'text-[rgb(var(--color-text-tertiary))]' },
    rolled_back: { icon: ArrowPathIcon, color: 'text-amber-400' },
  };

  const entry = config[status] || config.pending;
  const Icon = entry.icon;

  return (
    <div className="flex items-center gap-1.5">
      <Icon
        className={clsx('w-4 h-4', entry.color, status === 'running' && 'motion-loading-spin')}
      />
      <span className={clsx('text-xs font-medium', entry.color)}>{status}</span>
    </div>
  );
}
